import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  padding: 3rem;
  position: relative;
  overflow: hidden;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 1rem;
  text-align: center;
  max-width: 1000px;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #607C90;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  line-height: 1.6;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1400px;
  width: 100%;
  align-items: center;
`;

const FeaturesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #EF443C, #C43931);
  }
`;

const IconCircle = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #EF443C, #C43931);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  box-shadow: 0 6px 16px rgba(239, 68, 60, 0.3);
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureName = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.div`
  font-size: 1.1rem;
  color: #607C90;
  line-height: 1.5;
`;

const ScreenshotSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ScreenshotFrame = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(90deg, #042E4B, #0A4A6F);
    border-radius: 16px 16px 0 0;
  }
`;

const ScreenshotImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-top: 30px;
`;

const ScreenshotLabel = styled(motion.div)`
  background: linear-gradient(135deg, #042E4B, #0A4A6F);
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1rem;
  box-shadow: 0 6px 20px rgba(4, 46, 75, 0.3);
`;

const AIBadge = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  background: linear-gradient(135deg, #042E4B, #0A4A6F);
  color: white;
  padding: 1rem 2rem;
  border-radius: 30px;
  font-size: 1.2rem;
  font-weight: 700;
  box-shadow: 0 6px 20px rgba(4, 46, 75, 0.4);
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const Slide8IAWithScreenshot: React.FC = () => {
  const features = [
    {
      icon: '💡',
      name: 'Clareza Estratégica',
      description: 'Insights automáticos que revelam oportunidades ocultas nos seus dados',
    },
    {
      icon: '🚀',
      name: 'Agilidade Inteligente',
      description: 'Processos otimizados para decisões mais rápidas e eficientes',
    },
    {
      icon: '🎯',
      name: 'Decisões Assertivas',
      description: 'Recomendações precisas baseadas em análise preditiva',
    },
  ];

  return (
    <Container>
      <Title
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        Inteligência Artificial a favor da estratégia
      </Title>

      <Subtitle
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        A ThinkWay integra IA que gera insights automáticos em cada etapa do planejamento
      </Subtitle>

      <MainContent>
        <FeaturesSection>
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.name}
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{
                duration: 0.4,
                delay: 0.2 + index * 0.1,
                type: 'spring',
                stiffness: 80,
              }}
            >
              <IconCircle>{feature.icon}</IconCircle>
              <FeatureContent>
                <FeatureName>{feature.name}</FeatureName>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesSection>

        <ScreenshotSection>
          <ScreenshotFrame
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot11.png"
              alt="Insights com IA da ThinkWay"
            />
          </ScreenshotFrame>
          <ScreenshotLabel
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.7 }}
          >
            🤖 Insights Automáticos
          </ScreenshotLabel>
        </ScreenshotSection>
      </MainContent>

      <AIBadge
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.8 }}
      >
        <span>🧠</span>
        <span>Powered by AI</span>
      </AIBadge>
    </Container>
  );
};

export default Slide8IAWithScreenshot;